import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Shield, 
  CheckCircle, 
  FileText, 
  User, 
  Building2, 
  Loader2,
  AlertCircle,
  ArrowLeft
} from "lucide-react";

export const AcceptNDAPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [accepted, setAccepted] = useState(false);
  
  const [form, setForm] = useState({
    fullName: "",
    agreedToTerms: false
  });

  useEffect(() => {
    if (token) {
      fetchProposal();
    } else {
      setError("Invalid or missing proposal token");
      setLoading(false);
    }
  }, [token]);

  const fetchProposal = async () => {
    try {
      const res = await fetch(`${API}/assessment/nda/${token}`);
      if (res.ok) {
        const data = await res.json();
        setProposal(data);
        setForm(prev => ({ ...prev, fullName: data.lead_name || "" }));
      } else {
        const errData = await res.json();
        setError(errData.detail || "Proposal not found or expired");
      }
    } catch (err) {
      setError("Failed to load proposal details");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!form.fullName.trim()) {
      toast.error("Please enter your full name as signature");
      return;
    }
    if (!form.agreedToTerms) {
      toast.error("Please agree to the terms to continue");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/assessment/nda/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.fullName
        })
      });

      if (res.ok) {
        setAccepted(true);
        toast.success("NDA accepted! Check your email for login details.");
      } else {
        const errData = await res.json();
        toast.error(errData.detail || "Failed to accept NDA");
      }
    } catch (err) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-neutral-600">Loading proposal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">Proposal Not Available</h2>
            <p className="text-neutral-600 mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-2">NDA Accepted!</h2>
            <p className="text-neutral-600 mb-4">
              Your account has been created. Check your email for login instructions.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-amber-800 text-sm font-medium">
                📋 Next Step: Complete your Intake Form
              </p>
              <p className="text-amber-700 text-xs mt-1">
                Log in and complete the intake form so your consultant can prepare for your consultation.
              </p>
            </div>
            <Link to="/login">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Login & Complete Intake Form
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#39FF14] rounded-lg flex items-center justify-center">
              <span className="text-black font-bold">MB</span>
            </div>
            <div>
              <span className="text-white font-semibold">My Biz Consulting</span>
              <span className="text-neutral-400 text-sm ml-2">Business Solutions Center</span>
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-4">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">Confidential Business Discussion</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Mutual Non-Disclosure Agreement
          </h1>
          <p className="text-neutral-400 max-w-2xl mx-auto">
            Review and accept this agreement to begin your confidential consultation with My Biz Consulting.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Proposal Info */}
          <Card className="lg:col-span-1 bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg">Proposal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-xs text-neutral-400">Your Name</p>
                  <p className="text-white font-medium">{proposal?.lead_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-xs text-neutral-400">Business</p>
                  <p className="text-white font-medium">{proposal?.business_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-xs text-neutral-400">Assigned Consultant</p>
                  <p className="text-white font-medium">{proposal?.consultant_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* NDA Terms */}
          <Card className="lg:col-span-2 bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-neutral-600" />
                Agreement Terms
              </CardTitle>
              <CardDescription>
                Please read carefully before signing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="prose prose-sm max-w-none">
                <div className="bg-neutral-50 rounded-lg p-4 border max-h-64 overflow-y-auto text-sm text-neutral-700">
                  <h4 className="font-semibold mb-2">MUTUAL NON-DISCLOSURE AGREEMENT</h4>
                  
                  <p className="mb-3">
                    This Mutual Non-Disclosure Agreement ("Agreement") is entered into between 
                    <strong> My Biz Consulting</strong> and its partners ("Company") and 
                    <strong> {proposal?.lead_name}</strong> representing <strong>{proposal?.business_name}</strong> ("Recipient").
                  </p>

                  <h5 className="font-semibold mt-4 mb-2">1. Purpose</h5>
                  <p className="mb-3">
                    The parties wish to explore a potential business relationship regarding consulting services. 
                    In connection with this exploration, each party may disclose confidential information to the other party. 
                    <strong> This agreement is solely for the purpose of having confidential discussions and does not 
                    constitute an agreement for consulting services.</strong>
                  </p>

                  <h5 className="font-semibold mt-4 mb-2">2. Definition of Confidential Information</h5>
                  <p className="mb-3">
                    "Confidential Information" means any information disclosed by either party that is marked as confidential 
                    or that reasonably should be understood to be confidential given the nature of the information, including 
                    but not limited to: business plans, financial information, customer data, marketing strategies, 
                    proprietary methodologies, and trade secrets.
                  </p>

                  <h5 className="font-semibold mt-4 mb-2">3. Obligations</h5>
                  <p className="mb-3">
                    Each party agrees to: (a) hold the other party's Confidential Information in strict confidence; 
                    (b) not disclose such information to third parties without prior written consent; 
                    (c) use such information only for the purpose of evaluating the potential business relationship; 
                    (d) protect such information using the same degree of care used to protect its own confidential information.
                  </p>

                  <h5 className="font-semibold mt-4 mb-2">4. Exclusions</h5>
                  <p className="mb-3">
                    This Agreement does not apply to information that: (a) was publicly known at the time of disclosure; 
                    (b) becomes publicly known through no fault of the receiving party; (c) was already in the receiving 
                    party's possession prior to disclosure; (d) is required to be disclosed by law.
                  </p>

                  <h5 className="font-semibold mt-4 mb-2">5. Term</h5>
                  <p className="mb-3">
                    This Agreement shall remain in effect for a period of two (2) years from the date of acceptance. 
                    The obligations of confidentiality shall survive termination of this Agreement.
                  </p>

                  <h5 className="font-semibold mt-4 mb-2">6. No Obligation</h5>
                  <p className="mb-3">
                    <strong>Nothing in this Agreement shall obligate either party to enter into any further agreement 
                    or to proceed with any business relationship.</strong> This Agreement is solely for the protection 
                    of confidential information during preliminary discussions.
                  </p>

                  <p className="text-xs text-neutral-500 mt-4">
                    By signing below, you acknowledge that you have read, understood, and agree to be bound by the terms 
                    of this Mutual Non-Disclosure Agreement.
                  </p>
                </div>
              </div>

              {/* Signature Form */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-neutral-900 mb-4">Electronic Signature</h4>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Legal Name (as signature)</Label>
                    <Input
                      id="fullName"
                      value={form.fullName}
                      onChange={(e) => setForm(prev => ({ ...prev, fullName: e.target.value }))}
                      placeholder="Type your full name"
                      className="mt-1"
                    />
                    <p className="text-xs text-neutral-500 mt-1">
                      By typing your name, you agree this constitutes your electronic signature.
                    </p>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="terms"
                      checked={form.agreedToTerms}
                      onCheckedChange={(checked) => setForm(prev => ({ ...prev, agreedToTerms: checked }))}
                    />
                    <label htmlFor="terms" className="text-sm text-neutral-600 cursor-pointer">
                      I have read and agree to the terms of this Mutual Non-Disclosure Agreement. 
                      I understand this is an agreement for confidential discussions only, not a commitment to services.
                    </label>
                  </div>

                  <Button
                    onClick={handleAccept}
                    disabled={submitting || !form.fullName || !form.agreedToTerms}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    size="lg"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept Agreement & Create Account
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-neutral-500">
                    Date: {new Date().toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AcceptNDAPage;
